import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Button, IconButton, Popover, Stack } from "@mui/material";
import React, { forwardRef, useImperativeHandle, useMemo, useState } from "react";

import { labelMappings } from "config/LabelMapping";
import { QueryDetailsModal } from "features/queryDetailsModal/QueryDetailsModal";
import { useAppDispatch } from "hooks/store";
import { setIsFocusModalOpen, setUnsavedAnnotations } from "store/slices/FocusModal/focusModalSlice";
import { ListResponse } from "types/api";
import { DetectorModes } from "types/createDetector";
import { Detector } from "types/detectors";
import { ImageQuery } from "types/imageQueries";
import { isExplorePage } from "utils/utilityFunctions";

import { LabelButtons } from "../LabelButtons";

import { ClickOrigin, PageContext } from "./ImageQueryPreview";

interface ImageQueryActionsProps {
  imageQueries: ListResponse<ImageQuery> | undefined;
  detector: Detector | undefined;
  onRefetch: () => Promise<void>;
  pageContext: PageContext;
  anchorEl?: HTMLButtonElement | HTMLDivElement | null;
}

export interface ImageQueryActionsRef {
  onInQueueImageClick: (currentIndex: number, clickOrigin?: ClickOrigin) => void;
}

export const ImageQueryActions = forwardRef<ImageQueryActionsRef, ImageQueryActionsProps>(
  ({ detector, imageQueries, onRefetch, pageContext, anchorEl }: ImageQueryActionsProps, ref) => {
    useImperativeHandle(ref, () => ({
      onInQueueImageClick: (currentIndex: number, clickOrigin?: ClickOrigin) => {
        setGridIndex(currentIndex);
        dispatch(setUnsavedAnnotations(false));
        dispatch(setIsFocusModalOpen(true));
        setOpenState(shouldOpenPopover(isExplorePage(pageContext), currentIndex, clickOrigin) ? "popover" : "modal");
      },
    }));

    const [openState, setOpenState] = useState<"popover" | "modal" | "closed">("closed");
    const [loading, setLoading] = useState(false);
    const [gridIndex, setGridIndex] = useState(0);
    const dispatch = useAppDispatch();
    const selectedImage = useMemo(() => imageQueries?.results?.at(gridIndex), [gridIndex, imageQueries]);

    const handleRefetch = async () => {
      try {
        setLoading(true);
        await onRefetch();
        setLoading(false);
        handlePostRefetchTasks();
      } catch (error) {
        console.error("An error occurred during refetch:", error);
      }
    };

    const closeFocusModal = async () => {
      onRefetch();
      setOpenState("closed");
      dispatch(setIsFocusModalOpen(false));
    };

    const handlePostRefetchTasks = () => {
      if (!imageQueries?.results || imageQueries.results.length < 2) {
        closeFocusModal();
      }
      if ((imageQueries?.results?.length ?? 0) - 1 === gridIndex) {
        setGridIndex((prev) => prev - 1);
      }
    };

    const shouldOpenPopover = (isExplore: boolean, selectedImageIndex: number, clickOrigin?: ClickOrigin) => {
      const selected = imageQueries?.results.at(selectedImageIndex);

      if (!selected) return false;

      // Quick action is disabled for detector count
      if (detector?.mode === DetectorModes.COUNT) return false;
      if (clickOrigin === ClickOrigin.Canvas) return false;
      return isExplore;
    };

    if (!imageQueries) return null;
    if (openState === "closed") return null;
    if (!detector) return null;

    if (openState === "popover") {
      return (
        <Popover
          id="simple-popover"
          open={openState === "popover"}
          anchorEl={anchorEl}
          onClose={closeFocusModal}
          anchorOrigin={{
            vertical: -52,
            horizontal: "left",
          }}
        >
          <Stack direction="row" justifyContent="flex-start" spacing={1} className="px-3 py-2 items-center">
            <IconButton color="inherit" onClick={closeFocusModal} aria-label="close">
              <CloseIcon />
            </IconButton>
            <LabelButtons
              buttonLabels={labelMappings}
              posicheckId={selectedImage?.iq_id || ""}
              onLabel={closeFocusModal}
            />
            <Button
              style={{ textTransform: "none" }}
              variant="text"
              color="inherit"
              onClick={() => setOpenState("modal")}
              endIcon={<OpenInNewIcon />}
            >
              Query Details
            </Button>
          </Stack>
        </Popover>
      );
    }

    return (
      <QueryDetailsModal
        showDialog={openState === "modal"}
        loading={loading}
        getData={handleRefetch}
        rows={imageQueries?.results}
        rowCount={imageQueries?.results && imageQueries?.results.length!}
        selectedRowIndex={gridIndex}
        setSelectedRowIndex={setGridIndex}
        onClose={closeFocusModal}
        page={1}
        pageSize={12}
        setPage={() => {}}
        setPageSize={() => {}}
        detector={detector}
      />
    );
  }
);
